import { useTranslation } from "react-i18next";
import { formatCurrency } from "../../components/common/PriceDisplay";
import {
    AUTOCOMPLETE_FIELD_TYPE,
    DATE_RANGE_FIELD_TYPE,
    NUMERIC_RANGE_FIELD_TYPE,
    SELECT_FIELD_TYPE,
    TEXT_FIELD_TYPE,
} from "../../helpers/constants";
import {
    useAgreementKinds,
    useAgreementStatuses,
    useAgreementTypes,
} from "../../services/agreementsService";
import DateService from "../../services/dateService";
import { useInvestments, useProductTypes } from "../../services/productsService";
import { getDiscountedPricesForProduct, useLocalizedProperty } from "../../utils";
import { AgreementPassportButton } from "./AgreementPassportDialog/AgreementPassportButton";
import { AgreementStatusText } from "./AgreementStatusText";
import { useGetSellers } from "../../services/usersApi";
export const AGREEMENTS_TABLE_NAME = "agreements_table";

export const useAgreementsTableFieldsConfig = () => {
    const { t } = useTranslation(); 
    const getLocalizedProperty = useLocalizedProperty();

    const { data: investments } = useInvestments({
        select: (data) =>
            data.results.map((investment) => ({
                value: investment.id,
                label: investment.investment_name_sales,
            })),
    });

    const { data: productTypesOptions } = useProductTypes(undefined, {
        select: (data) =>
            data.map((type) => ({
                id: type.id,
                label: getLocalizedProperty(type, "name"),
            })),
    });

    const { data: agreementTypes } = useAgreementTypes({
        select: (data) =>
            data.map((type) => ({
                value: type.id,
                label: getLocalizedProperty(type, "agretype_name"),
            })),
    });

    const { data: agreementKinds } = useAgreementKinds({
        select: (data) =>
            data.map((kind) => ({
                value: kind.id,
                label: getLocalizedProperty(kind, "agrekind_name"),
            })),
    });

    const { data: agreementStatuses } = useAgreementStatuses({
        select: (data) =>
            data.map((status) => ({
                value: status.status_name_eng,
                label: getLocalizedProperty(status, "status_name") || status.status_name_eng || "",
            })),
    });

    const { data: sellers } = useGetSellers({
        select: (data) =>
            data.map((seller) => ({
                value: seller.id,
                label: `${seller.full_name}`,
            })),
    });

    return [
        {
            name: "agreement_nr",
            getValueCallback: (rowData) => rowData.agreement_nr,
            label: t("table.agreements.agreementNr"),
            filterType: TEXT_FIELD_TYPE,
            getFilterFieldConfigCallback: () => undefined,
            sortable: true,
        },
        {
            name: "customer_names",
            getValueCallback: (rowData) =>
                rowData.shares
                    .flatMap((s) => s.customers)
                    .map((c) => c.person?.name || "")
                    .filter(Boolean)
                    .join("\n"),
            label: t("table.agreements.customerNames"),
            filterType: TEXT_FIELD_TYPE,
            getFilterFieldConfigCallback: () => undefined,
            sortable: false,
        },
        {
            name: "customer_last_names",
            getValueCallback: (rowData) =>
                rowData.shares
                    .flatMap((s) => s.customers)
                    .map((c) => c.person?.surname || "")
                    .filter(Boolean)
                    .join("\n"),
            label: t("table.agreements.customerSurnames"),
            filterType: TEXT_FIELD_TYPE,
            getFilterFieldConfigCallback: () => undefined,
            sortable: false,
        },
        {
            name: "counterparty_names",
            getValueCallback: (rowData) =>
                rowData.shares
                    .flatMap((s) => s.customers)
                    .map((c) => c.company?.name || "")
                    .filter(Boolean)
                    .join("\n"),
            label: t("table.agreements.companyNames"),
            filterType: TEXT_FIELD_TYPE,
            getFilterFieldConfigCallback: () => undefined,
            sortable: false,
        },
        {
            name: "investment",
            getValueCallback: (rowData) =>
                // Prefer investment from product if present, fallback to id mapping
                rowData.products.length > 0
                    ? rowData.products
                          .map((ap) => ap.product.investment?.investment_name_sales || "-")
                          .join("\n")
                    : investments?.find((i) => i.value === rowData.agreement_invest)?.label || "",
            label: t("table.agreements.investment"),
            filterType: SELECT_FIELD_TYPE,
            getFilterFieldConfigCallback: () => ({
                optionKey: "value",
                optionLabel: "label",
                options: investments,
                multiple: false,
            }),
            fieldConfig: {
                optionKey: "value",
                optionLabel: "label",
                options: investments,
            },
            sortable: false,
        },
        {
            name: "agreement_type",
            getValueCallback: (rowData) =>
                getLocalizedProperty(rowData.agreement_type, "agretype_name") ||
                rowData.agreement_type?.agretype_name_eng ||
                "-",
            label: t("table.agreements.agreementType"),
            filterType: SELECT_FIELD_TYPE,
            getFilterFieldConfigCallback: () => ({
                optionKey: "value",
                optionLabel: "label",
                options: agreementTypes,
                multiple: false,
            }),
            fieldConfig: {
                optionKey: "value",
                optionLabel: "label",
                options: agreementTypes,
            },
            sortable: true,
        },
        {
            name: "agreement_kind",
            getValueCallback: (rowData) =>
                getLocalizedProperty(rowData.agreement_kind, "agrekind_name") ||
                rowData.agreement_kind?.agrekind_name_eng ||
                "-",
            label: t("table.agreements.agreementKind"),
            filterType: SELECT_FIELD_TYPE,
            getFilterFieldConfigCallback: () => ({
                optionKey: "value",
                optionLabel: "label",
                options: agreementKinds,
                multiple: false,
            }),
            fieldConfig: {
                optionKey: "value",
                optionLabel: "label",
                options: agreementKinds,
            },
            sortable: true,
        },
        {
            name: "agreement_status",
            getValueCallback: (rowData) => (
                <AgreementStatusText status={rowData.agreement_status} />
            ),
            label: t("table.agreements.status"),
            filterType: SELECT_FIELD_TYPE,
            sortable: true,
            getFilterFieldConfigCallback: (filteringData) => ({
                options: agreementStatuses,
                optionKey: "value",
                optionLabel: "label",
            }),
            fieldConfig: {
                optionKey: "value",
                optionLabel: "label",
                options: agreementStatuses,
            },
        },
        {
            name: "agreement_date_signed",
            getValueCallback: (rowData) =>
                rowData.agreement_date_signed
                    ? DateService.convertDatetimeFromBackendToFormatYYYYMMDD(
                          rowData.agreement_date_signed,
                      )
                    : "",
            label: t("table.agreements.agreementDateSigned"),
            filterType: DATE_RANGE_FIELD_TYPE,
            getFilterFieldConfigCallback: () => ({ style: { minWidth: "100px" } }),
            sortable: true,
        },
        {
            name: "agreement_notarial_deed",
            getValueCallback: (rowData) => rowData.agreement_notarial_deed || "",
            label: t("table.agreements.agreementNotarialDeed"),
            filterType: TEXT_FIELD_TYPE,
            getFilterFieldConfigCallback: () => undefined,
            sortable: true,
        },
       {
            name: "product_types_ids",
            other_export_field_name: "product_types",
            getValueCallback: (rowData) =>
                rowData.products
                    .map((ap) => ap.product.type)
                    .map((type) => (type ? getLocalizedProperty(type, "name") : "–"))
                    .join("\n"),
            label: t("table.agreements.productTypes"),
            filterType: AUTOCOMPLETE_FIELD_TYPE,
            other_export_field_name: "product_types",
            label: t("products.columns.productType"),
            filterType: AUTOCOMPLETE_FIELD_TYPE,
            minWidth: 120,
            width: 120,
            fieldConfig: {
                optionLabelKey: "label",
                options: productTypesOptions || [],
                multiple: true,
                isObjectOption: true,
                disableClearable: false
            },
        },
        {
            name: "product_numbers",
            getValueCallback: (rowData) =>
                rowData.products.map((ap) => ap.product.product_number || "-").join("\n"),
            label: t("table.agreements.productNumbers"),
            filterType: TEXT_FIELD_TYPE,
            getFilterFieldConfigCallback: () => undefined,
            sortable: false,
        },
        {
            name: "agreement_note_sales",
            getValueCallback: (rowData) => rowData.agreement_note_sales || "",
            label: t("table.agreements.note"),
            filterType: TEXT_FIELD_TYPE,
            getFilterFieldConfigCallback: () => undefined,
            sortable: true,
        },
        {
            name: "product_discounted_gross_prices",
            getValueCallback: (rowData) =>
                rowData.products
                    .map((ap) =>
                        formatCurrency(
                            getDiscountedPricesForProduct(ap.product, ap.discount_proc).gross,
                        ),
                    )
                    .join("\n"),
            label: t("table.agreements.discountedGross"),
            sortable: false,
            filterType: NUMERIC_RANGE_FIELD_TYPE,
            getFilterFieldConfigCallback: () => undefined,
        },
        {
            name: "product_discounted_gross_per_sqm_prices",
            getValueCallback: (rowData) =>
                rowData.products
                    .map((ap) =>
                        formatCurrency(
                            getDiscountedPricesForProduct(ap.product, ap.discount_proc)
                                .gross_per_sqm,
                        ),
                    )
                    .join("\n"),
            label: t("table.agreements.discountedGrossPerSqm"),
            sortable: false,
            filterType: NUMERIC_RANGE_FIELD_TYPE,
            getFilterFieldConfigCallback: () => undefined,
        },
        {
            name: "product_discounted_net_prices",
            getValueCallback: (rowData) =>
                rowData.products
                    .map((ap) =>
                        formatCurrency(
                            getDiscountedPricesForProduct(ap.product, ap.discount_proc).net,
                        ),
                    )
                    .join("\n"),
            label: t("table.agreements.discountedNet"),
            sortable: false,
            filterType: NUMERIC_RANGE_FIELD_TYPE,
            getFilterFieldConfigCallback: () => undefined,
        },
        {
            name: "product_discounted_net_per_sqm_prices",
            getValueCallback: (rowData) =>
                rowData.products
                    .map((ap) =>
                        formatCurrency(
                            getDiscountedPricesForProduct(ap.product, ap.discount_proc).net_per_sqm,
                        ),
                    )
                    .join("\n"),
            label: t("table.agreements.discountedNetPerSqm"),
            sortable: false,
            filterType: NUMERIC_RANGE_FIELD_TYPE,
            getFilterFieldConfigCallback: () => undefined,
        },
        {
            name: "agreement_notarial_plan_date",
            getValueCallback: (rowData) =>
                rowData.agreement_notarial_plan_date
                    ? DateService.convertDatetimeFromBackendToFormatYYYYMMDD(
                          rowData.agreement_notarial_plan_date,
                      )
                    : "",
            label: t("table.agreements.agreementNotarialPlanDate"),
            filterType: DATE_RANGE_FIELD_TYPE,
            getFilterFieldConfigCallback: () => ({ style: { minWidth: "100px" } }),
            sortable: true,
        },
        {
            name: "created",
            getValueCallback: (rowData) =>
                rowData.created
                    ? DateService.convertDatetimeFromBackendToFormatYYYYMMDD(rowData.created)
                    : "",
            label: t("table.agreements.created"),
            filterType: DATE_RANGE_FIELD_TYPE,
            getFilterFieldConfigCallback: () => ({ style: { minWidth: "100px" } }),
            sortable: true,
        },
        {
            name: "agreement_status_reason",
            getValueCallback: (rowData) =>
                getLocalizedProperty(rowData.agreement_status_reason, "reason_name") || "",
            label: t("table.agreements.agreementStatusReason"),
            filterType: TEXT_FIELD_TYPE,
            getFilterFieldConfigCallback: () => undefined,
            sortable: true,
        },
        {
            name: "agreement_status_note",
            getValueCallback: (rowData) => rowData.agreement_status_note,
            label: t("table.agreements.agreementStatusNote"),
            filterType: TEXT_FIELD_TYPE,
            getFilterFieldConfigCallback: (_) => undefined,
        },
        {
            name: "agreement_seller",
            getValueCallback: (rowData) => rowData.agreement_seller?.full_name,
            label: t("table.agreements.seller"),
            filterType: SELECT_FIELD_TYPE,
            getFilterFieldConfigCallback: (filteringData) => ({
                optionKey: "value",
                optionLabel: "label",
                options: sellers,
                multiple: false,
            }),
            fieldConfig: {
                optionKey: "value",
                optionLabel: "label",
                options: sellers,
            },
        },
        {
            name: "passport",
            getValueCallback: (rowData) => <AgreementPassportButton agreement={rowData} />,
            label: t("table.agreements.passport"),
            getFilterFieldConfigCallback: () => undefined,
            sortable: false,
            excludedFromExport: true,
            allowChangeVisibility: false,
        },
    ];
};
